import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';

import { useTiersService } from '@europrocurement/l2d-domain';
import { type DataProviderProps, type DataState } from '@europrocurement/l2d-modules/providers';
import { GetNotesContactContactsItem200ResponseNotesInner } from '@europrocurement/l2d-domain/openApi/ApiTiers';

type CurrentPublisherProviderState = DataState & {
    data:
        | {
              id: number | null;
              notes: GetNotesContactContactsItem200ResponseNotesInner[];
          }
        | undefined;
    currentIdPublisher: number | null | undefined;
};

type CurrentPublisherProviderProps = DataProviderProps & {
    idPublisher: string;
};

const INIT_CURRENT_PUBLISHER_CTX = 'init_current_publisher_ctx';
const SET_CURRENT_PUBLISHER_CTX = 'set_current_publisher_ctx';
const SET_CURRENT_PUBLISHER_NOTES_CTX = 'set_current_publisher_notes_ctx';
const REFRESH_CURRENT_PUBLISHER_CTX = 'refresh_current_publisher_ctx';

type Action =
    | {
          type: 'init_current_publisher_ctx';
          payload: { id: number; notes: GetNotesContactContactsItem200ResponseNotesInner[] };
      }
    | {
          type: 'set_current_publisher_ctx';
          payload: { id: number; notes: GetNotesContactContactsItem200ResponseNotesInner[] };
      }
    | {
          type: 'set_current_publisher_notes_ctx';
          payload: GetNotesContactContactsItem200ResponseNotesInner[];
      }
    | { type: 'refresh_current_publisher_ctx' };

const CurrentPublisherContext = createContext<CurrentPublisherProviderState | null>(null);
const CurrentPublisherDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function CurrentPublisherReducer(
    state: CurrentPublisherProviderState,
    action: Action,
): CurrentPublisherProviderState {
    switch (action.type) {
        case INIT_CURRENT_PUBLISHER_CTX: {
            return {
                ...state,
                loaded: true,
                currentIdPublisher: action.payload.id,
                data: action.payload,
            };
        }

        case SET_CURRENT_PUBLISHER_CTX: {
            return {
                ...state,
                data: action.payload,
            };
        }

        case SET_CURRENT_PUBLISHER_NOTES_CTX: {
            return {
                ...state,
                data: {
                    ...state.data,
                    notes: action.payload,
                } as CurrentPublisherProviderState['data'],
            };
        }

        case REFRESH_CURRENT_PUBLISHER_CTX: {
            return {
                ...state,
                loaded: false,
            };
        }
        default: {
            const exhaustiveCheck: never = action;
            throw new Error(`Unknown action: ${exhaustiveCheck}`);
        }
    }
}

const CurrentPublisherProvider: React.FunctionComponent<
    PropsWithChildren<CurrentPublisherProviderProps>
> = function ({ autoload, children, idPublisher }) {
    const [state, dispatch] = useReducer(CurrentPublisherReducer, {
        loaded: false,
        data: undefined,
        currentIdPublisher: undefined,
    } as CurrentPublisherProviderState);

    const { publishersApi } = useTiersService();
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const fetchAndInitPublisher = React.useCallback(
        async (publisherId: string) => {
            setIsFetching(true);

            try {
                const publisherIntId = parseInt(publisherId, 10);

                const notesResult = await publishersApi.getNotesFournisseurFournisseurItem({
                    id: publisherIntId,
                    limit: 100,
                });

                const notes = notesResult.data.notes || [];

                const publisherState = {
                    id: publisherIntId,
                    notes,
                };

                dispatch({
                    type: INIT_CURRENT_PUBLISHER_CTX,
                    payload: publisherState,
                });
            } catch (error) {
                console.error(error);
            } finally {
                setIsFetching(false);
            }
        },
        [publishersApi],
    );

    useEffect(() => {
        if (!autoload || state.loaded === true || isFetching === true) {
            return;
        }
        fetchAndInitPublisher(idPublisher);
    }, [publishersApi, autoload, state.loaded, isFetching, fetchAndInitPublisher, idPublisher]);

    useEffect(() => {
        if (
            state.currentIdPublisher &&
            state.currentIdPublisher !== parseInt(idPublisher, 10) &&
            autoload &&
            state.loaded === true &&
            !isFetching
        ) {
            dispatch({
                type: REFRESH_CURRENT_PUBLISHER_CTX,
            });
        }
    }, [autoload, idPublisher, isFetching, state.currentIdPublisher, state.loaded]);

    return (
        <CurrentPublisherContext.Provider value={state}>
            <CurrentPublisherDispatchContext.Provider value={dispatch}>
                {children}
            </CurrentPublisherDispatchContext.Provider>
        </CurrentPublisherContext.Provider>
    );
};

const useCurrentPublisherCtx = () => {
    const state = useContext(CurrentPublisherContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getCurrentPublisher = () => state?.data;

    return {
        publisher: state?.data,
        isDataLoaded,
        getCurrentPublisher,
    };
};

const useCurrentPublisherCtxDispatch = () => useContext(CurrentPublisherDispatchContext);

export {
    CurrentPublisherProvider,
    CurrentPublisherContext,
    CurrentPublisherDispatchContext,
    useCurrentPublisherCtx,
    useCurrentPublisherCtxDispatch,
    INIT_CURRENT_PUBLISHER_CTX,
    SET_CURRENT_PUBLISHER_CTX,
    SET_CURRENT_PUBLISHER_NOTES_CTX,
    REFRESH_CURRENT_PUBLISHER_CTX,
};
